import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  Wrench,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Clock,
  TrendingUp,
  FileText
} from 'lucide-react'
import DataTable from '../../../components/DataTable/DataTable'
import Breadcrumb from '../../../components/Common/Breadcrumb'
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import changeRequestService from '../../../services/changeRequestService'
import '../../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const ChangeRequestList = () => {
  const navigate = useNavigate()
  const [selectedRows, setSelectedRows] = useState([])
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalChangeRequests: 0,
    draftChangeRequests: 0,
    submittedChangeRequests: 0,
    underReviewChangeRequests: 0,
    approvedChangeRequests: 0,
    rejectedChangeRequests: 0,
    inProgressChangeRequests: 0,
    completedChangeRequests: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [changeRequestsResponse, statsResponse] = await Promise.all([
        changeRequestService.getAllChangeRequests({ limit: 1000 }),
        changeRequestService.getChangeRequestStats()
      ])

      if (changeRequestsResponse && changeRequestsResponse.success) {
        const changeRequests = changeRequestsResponse.changeRequests || []
        setData(changeRequests.map(cr => ({
          ...cr,
          id: cr._id || cr.id
        })))
        
        // Show toast if no data
        if (changeRequests.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No change requests found. Create your first change request to get started!')
        }
      } else {
        toast.error(changeRequestsResponse?.message || 'Failed to load change requests')
      }

      if (statsResponse && statsResponse.success) {
        setStats(statsResponse.data || {})
      }
    } catch (error) {
      console.error('Error loading data:', error)
      toast.error('Failed to load change requests data')
    } finally {
      setLoading(false)
    }
  }

  const handleAddNew = () => {
    navigate('/projects/change-requests/new')
  }

  const handleEdit = (changeRequest) => {
    navigate(`/projects/change-requests/edit/${changeRequest._id || changeRequest.id}`)
  }

  const handleView = (changeRequest) => {
    navigate(`/projects/change-requests/view/${changeRequest._id || changeRequest.id}`)
  }

  const handleDelete = (changeRequest) => {
    setDeleteItem(changeRequest)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      const response = await changeRequestService.deleteChangeRequest(deleteItem._id || deleteItem.id)
      if (response.success) {
        toast.success('Change request deleted successfully')
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete change request')
      }
    } catch (error) {
      console.error('Error deleting change request:', error)
      toast.error('Failed to delete change request')
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const handleRowSelection = (selectedRows) => {
    setSelectedRows(selectedRows)
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const day = String(date.getDate()).padStart(2, '0')
    const month = months[date.getMonth()]
    const year = date.getFullYear()
    return `${day}-${month}-${year}`
  }

  // Define columns for the data table
  const columns = [
    {
      key: 'changeRequestNumber',
      label: 'CR #',
      sortable: true,
      render: (value, row) => (
        <div className="font-medium text-gray-900">
          {value || 'N/A'}
        </div>
      )
    },
    {
      key: 'title',
      label: 'Title',
      sortable: true,
      render: (value, row) => (
        <div className="font-medium text-gray-900" style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value || 'N/A'}
        </div>
      )
    },
    {
      key: 'projectName',
      label: 'Project',
      sortable: true,
      render: (value, row) => (
        <div className="font-medium text-gray-900">{value || 'N/A'}</div>
      )
    },
    {
      key: 'changeType',
      label: 'Type',
      sortable: true,
      render: (value, row) => (
        <div className="font-medium text-gray-900">{value || 'N/A'}</div>
      )
    },
    {
      key: 'priority',
      label: 'Priority',
      sortable: true,
      render: (value, row) => {
        const priority = value || 'Medium'
        const priorityColors = {
          'Low': '#10B981',
          'Medium': '#F59E0B',
          'High': '#EF4444',
          'Critical': '#DC2626'
        }
        const priorityColor = priorityColors[priority] || '#6B7280'
        
        return (
          <span 
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
            style={{ 
              backgroundColor: `${priorityColor}20`,
              color: priorityColor,
              border: `1px solid ${priorityColor}40`
            }}
          >
            {priority}
          </span>
        )
      }
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value, row) => {
        const status = value || 'Draft'
        const statusColors = {
          'Draft': '#6B7280',
          'Submitted': '#3B82F6',
          'Under Review': '#F59E0B',
          'Approved': '#10B981',
          'Rejected': '#EF4444',
          'In Progress': '#8B5CF6',
          'Completed': '#10B981',
          'Cancelled': '#DC2626'
        }
        const statusColor = statusColors[status] || '#6B7280'
        
        return (
          <span 
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
            style={{ 
              backgroundColor: `${statusColor}20`,
              color: statusColor,
              border: `1px solid ${statusColor}40`
            }}
          >
            {status}
          </span>
        )
      }
    },
    {
      key: 'requestedByName',
      label: 'Requested By',
      sortable: true,
      render: (value, row) => (
        <div className="font-medium text-gray-900">{value || 'N/A'}</div>
      )
    },
    {
      key: 'requestDate',
      label: 'Request Date',
      sortable: true,
      render: (value, row) => {
        if (!value) return <span className="text-gray-500">N/A</span>
        return <div className="font-medium text-gray-900">{formatDate(value)}</div>
      }
    }
  ]

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'Projects',
      path: '/projects',
      onClick: () => navigate('/projects')
    },
    {
      label: 'Transactions',
      path: '/projects?tab=transactions',
      onClick: () => navigate('/projects?tab=transactions')
    },
    {
      label: 'Change Requests',
      path: '/projects/change-requests',
      isCurrent: true
    }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Change Requests</h1>
          <p>Create, view, and manage project change requests</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn-primary"
            onClick={handleAddNew}
          >
            <Plus size={16} />
            Add New Change Request
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <FileText size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+0%</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalChangeRequests || 0}</div>
          <div className="metric-title">Total Change Requests</div>
          <div className="metric-subtitle">All time</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+0%</span>
            </div>
          </div>
          <div className="metric-value">{stats.approvedChangeRequests || 0}</div>
          <div className="metric-title">Approved</div>
          <div className="metric-subtitle">Ready for implementation</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Clock size={16} />
            </div>
            <div className="metric-trend negative">
              <TrendingUp size={12} />
              <span>+0%</span>
            </div>
          </div>
          <div className="metric-value">{stats.underReviewChangeRequests || 0}</div>
          <div className="metric-title">Under Review</div>
          <div className="metric-subtitle">Pending approval</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <AlertTriangle size={16} />
            </div>
            <div className="metric-trend negative">
              <TrendingUp size={12} />
              <span>+0%</span>
            </div>
          </div>
          <div className="metric-value">{stats.draftChangeRequests || 0}</div>
          <div className="metric-title">Draft</div>
          <div className="metric-subtitle">In preparation</div>
        </div>
      </div>

      {/* Change Request List Table */}
      <div className="dashboard-content">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          searchPlaceholder="Search change requests..."
          emptyMessage="No change requests found. Create your first change request to get started!"
          noCard={true}
        />
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Change Request"
        message={`Are you sure you want to delete "${deleteItem?.changeRequestNumber || deleteItem?.title || ''}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default ChangeRequestList

